import {useEffect, useState} from 'react';

import type {TestIdValue} from '../types';
import {logger} from '../logger';
import {brandingLngs, ALLOWED_LANGUAGES, initI18next} from '../i18n';
import {config} from '../config';
import {CALL_TYPE, SHADES} from '../constants';
import {setApplicationConfig} from '../applicationConfig';
import {transformHiddenFunctionalityArrayToObject} from '../utils/transformHiddenFunctionalityArrayToObject';
import {parseLegacyParams} from '../utils/parseLegacyParams';

import type {Favicon, Manifest} from '.';
import {getBrandingPath, DEFAULT, loadBranding} from '.';
import {getClientCallType} from '../utils/getClientCallType';

const setBrandApplicationConfig = (
    brandingApplicationConfig: Manifest['applicationConfig'] = {},
) => {
    for (const [key, value] of Object.entries(brandingApplicationConfig)) {
        setApplicationConfig(
            key as keyof Manifest['applicationConfig'],
            key === 'hiddenFunctionality'
                ? transformHiddenFunctionalityArrayToObject(
                      value as TestIdValue[],
                  )
                : value,
        );
    }
};

const setBrandDefaultUserConfig = (
    defaultUserConfig: Manifest['defaultUserConfig'] = {},
) => {
    for (const [k, value] of Object.entries(defaultUserConfig)) {
        const key = k as keyof Manifest['defaultUserConfig'];
        // Apply only for unchanged defaults
        if (config.isDefaultValue(key)) {
            if (key === 'callType' && typeof value === 'string') {
                config.set({key, value: getClientCallType(value) ?? CALL_TYPE});
                continue;
            }
            config.set({key, value});
        }
    }
};

const setBrandPalette = (colorPalette: Manifest['colorPalette']) => {
    if (colorPalette && colorPalette.length > 0) {
        if (colorPalette.length !== SHADES.length) {
            logger.warn(
                'color palette has too many/few elements. check your branding manifest version.',
            );
        }
        colorPalette.forEach((color, idx) => {
            document.documentElement.style.setProperty(
                `--color-brand-${SHADES[idx]}`,
                color,
            );
        });
    } else {
        for (const shade of SHADES) {
            document.documentElement.style.removeProperty(
                `--color-brand-${shade}`,
            );
        }
    }
};

const setBrandBgColor = (backgroundColor: Manifest['backgroundColor']) => {
    if (backgroundColor) {
        document.documentElement.style.setProperty(
            '--color-brand-background',
            backgroundColor,
        );
    } else {
        document.documentElement.style.removeProperty(
            '--color-brand-background',
        );
    }
};

const setBrandApplicationTitle = (appTitle: Manifest['appTitle']) => {
    if (appTitle) {
        document.title = appTitle;
    }
};

const setBgReplacementAssets = (
    bgImageAssets: Manifest['applicationConfig']['bgImageAssets'],
    backgroundImageUrl: Manifest['defaultUserConfig']['bgImageUrl'],
) => {
    // Normalization of the old brands, makes sure we always have bgImageAssets if bgImageUrl is specified
    if (backgroundImageUrl && !bgImageAssets) {
        setApplicationConfig('bgImageAssets', [backgroundImageUrl]);
    }
};

const setFavicon = (favicon?: Favicon) => {
    if (!favicon || !('href' in favicon)) {
        return;
    }

    // Remove all existing favicon and apple-touch-icon links
    document
        .querySelectorAll('link[rel="icon"], link[rel="apple-touch-icon"]')
        .forEach(el => {
            el.remove();
        });

    const link = document.createElement('link');
    link.rel = 'icon';
    if (favicon.type) {
        link.type = favicon.type;
    }
    if (favicon.sizes) {
        link.sizes.add(favicon.sizes);
    }
    link.href = getBrandingPath(favicon.href);
    document.head.appendChild(link);
};

const isValidAvailableLanguages = (
    availableLngs: string[] | undefined,
    allLngs: string[],
) => {
    if (!availableLngs || availableLngs.length === 0) {
        return false;
    }
    return availableLngs.some(lng => allLngs.includes(lng));
};

export const setLanguages = (
    availableLanguages: Manifest['availableLanguages'],
    translations: Manifest['translations'],
) => {
    let allLanguages = [
        ...ALLOWED_LANGUAGES,
        ...Object.keys(translations).filter(
            k => !ALLOWED_LANGUAGES.includes(k),
        ),
    ];

    for (const [lng, path] of Object.entries(translations)) {
        brandingLngs.set(lng, getBrandingPath(path));
    }

    // If available languages are defined: All languages = available languages
    if (isValidAvailableLanguages(availableLanguages, allLanguages)) {
        allLanguages = allLanguages.filter(lng =>
            availableLanguages?.includes(lng),
        );
    }

    void initI18next(allLanguages, true);

    return allLanguages;
};

export function useBrandingLoader() {
    const [brand, setBrand] = useState<Manifest>();
    useEffect(() => {
        let ignore = false;
        // TODO: How do we handle missing branding?
        loadBranding()
            .then(brand => {
                if (!ignore) {
                    setBrand(brand);
                }
            })
            .catch((error: unknown) => {
                if (!ignore) {
                    setBrand(DEFAULT);
                }
                logger.warn({error}, 'failed to load branding');
            });
        return () => {
            ignore = true;
        };
    }, []);

    useEffect(() => {
        if (brand) {
            try {
                setBrandApplicationConfig(brand.applicationConfig);
                setFavicon(brand.favicon);
                setBrandDefaultUserConfig(brand.defaultUserConfig);
                setBrandApplicationTitle(brand.appTitle);
                setBrandPalette(brand.colorPalette);
                setBrandBgColor(brand.backgroundColor);
                setBgReplacementAssets(
                    brand.applicationConfig?.bgImageAssets,
                    brand.defaultUserConfig?.bgImageUrl,
                );
                setLanguages(brand.availableLanguages, brand.translations);

                // always prioritise query params
                const paramsString =
                    location.search.substring(1) || location.hash.substring(3);
                if (!paramsString) {
                    return;
                }
                parseLegacyParams(paramsString);
            } catch (error: unknown) {
                setBrand(DEFAULT);
                logger.error({error}, 'branding manifest is invalid');
            }
        }
    }, [brand]);

    return brand;
}
